import React from 'react';
import JobCategoryPage from './JobCategoryPage';
import { Heart } from 'lucide-react';

const HospiceSocialWorkerJobs = () => {
  const contentSections = [
    {
      title: "What is a Hospice Social Worker?",
      content: (
        <p>
          Hospice social workers are specialized licensed professionals who provide psychosocial support to patients with terminal illnesses and their families during end-of-life care. Working as integral members of hospice interdisciplinary teams, these compassionate professionals address the emotional, social, spiritual, and practical needs that arise when someone is facing a life-limiting diagnosis. Hospice social workers help patients achieve comfort and dignity in their final days while supporting families through anticipatory grief and bereavement.
        </p>
      )
    },
    {
      title: "Hospice Social Worker Job Responsibilities",
      content: (
        <p>
          Hospice social workers provide holistic support that addresses the full spectrum of needs during end-of-life care. Their work spans clinical counseling, care coordination, resource navigation, and family support services.
        </p>
      ),
      list: [
        "Conduct comprehensive psychosocial assessments for patients and families",
        "Provide individual and family counseling for anticipatory grief",
        "Assist with advance care planning and healthcare proxy documentation",
        "Coordinate community resources and practical support services",
        "Facilitate family meetings and care conferences",
        "Support patients in achieving end-of-life goals and legacy projects",
        "Provide bereavement support to families after patient death",
        "Document clinical notes and participate in interdisciplinary team meetings",
        "Assist with funeral planning and practical arrangements",
        "Navigate insurance, Medicare/Medicaid, and financial assistance programs"
      ]
    },
    {
      title: "Required Qualifications and Licenses",
      content: (
        <p>
          Hospice social work positions typically require strong clinical skills and comfort with end-of-life care. Most hospice agencies require MSW-level practitioners, with LCSW or LMSW licensure depending on state requirements and position level. Medicare-certified hospices have specific requirements for social work staffing.
        </p>
      ),
      list: [
        "Master of Social Work (MSW) from a CSWE-accredited program",
        "State clinical licensure (LCSW preferred, LMSW considered)",
        "Experience or coursework in end-of-life care, grief, and loss",
        "Knowledge of Medicare hospice benefit and regulations",
        "Excellent communication and documentation skills",
        "Comfort discussing death, dying, and spiritual matters",
        "Valid driver's license for home visits (most positions)"
      ]
    },
    {
      title: "Salary and Benefits for Hospice Social Workers",
      content: (
        <p>
          Hospice social worker salaries typically range from $50,000 to $75,000+ depending on experience, location, and organization type. Many hospice agencies are nonprofit organizations offering competitive benefits including health insurance, retirement plans, and generous PTO. Mileage reimbursement is standard for positions requiring home visits. Some hospices offer flexible schedules, remote work options for documentation, and professional development support. The growing demand for hospice services and aging population trends suggest strong job security and opportunities for advancement in this specialty.
        </p>
      )
    },
    {
      title: "Work Environment and Settings",
      content: (
        <p>
          Hospice social workers provide care in various settings where patients receive end-of-life care. This includes patients' private homes, assisted living facilities, nursing homes, and inpatient hospice units. The role typically involves traveling between patient locations, with some office-based administrative time. Many hospice agencies offer flexible scheduling and the ability to manage your own caseload. While the work involves emotional challenges, hospice teams are known for their strong peer support and commitment to self-care.
        </p>
      )
    },
    {
      title: "Why Choose Hospice Social Work?",
      content: (
        <p>
          Hospice social work offers a profound opportunity to support individuals and families during life's most sacred moments. Social workers in this specialty describe their work as deeply meaningful, providing comfort and dignity when it matters most. The interdisciplinary team environment, meaningful patient relationships, and ability to make a tangible difference attract compassionate professionals to this field. While emotionally demanding, hospice social workers consistently report high job satisfaction and a sense of purpose that sustains their careers. For those called to serve during life's final chapter, hospice social work is an incredibly rewarding specialty.
        </p>
      )
    }
  ];

  const relatedCategories = [
    { name: "Hospital Social Worker Jobs", href: "/hospital-social-worker-jobs" },
    { name: "Medical Social Worker Jobs", href: "/medical-social-worker-jobs" },
    { name: "Palliative Care Social Worker Jobs", href: "/palliative-care-social-worker-jobs" },
    { name: "Home Health Social Worker Jobs", href: "/home-health-social-worker-jobs" },
  ];

  return (
    <JobCategoryPage
      category="hospice-social-worker-jobs"
      title="Hospice Social Worker Jobs"
      metaTitle="Hospice Social Worker Jobs | End-of-Life Care Positions | INeedASocialWorker"
      metaDescription="Find hospice social worker jobs nationwide. Provide compassionate end-of-life care and bereavement support. LCSW and LMSW positions available. Apply with SW Express Apply™."
      heroTitle="Hospice Social Worker Jobs"
      heroSubtitle="Provide compassionate end-of-life support with hospice social work positions at agencies and organizations across the United States."
      searchKeywords={["hospice social worker", "hospice social work", "end of life social worker", "palliative social worker", "bereavement social worker", "hospice care"]}
      contentSections={contentSections}
      relatedCategories={relatedCategories}
      iconComponent={Heart}
    />
  );
};

export default HospiceSocialWorkerJobs;
